import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { sharedObtenerIcon } from "../../../utils/shared-functions";
import { obtenerHistorico } from "../../../api/api-seguimiento";
import { useTranslation } from "react-i18next";
import Calendar from "../../../components/calendar";
import { SlidersHorizontal, X } from "lucide-react";
import { sharedObtenerIdEmpresa } from "../../../utils/shared-functions";
import { obtenerReporteInfoKilometraje } from "../../../api/gestion-dts/api-reportes";

// Marker (pin) size in pixels — change these to resize the delivery/client markers (not puntoActual)
const PIN_WIDTH = 32;
const PIN_HEIGHT = 36;
const LABEL_OFFSET_Y = 4; // title immediately below pin tip (small gap)

// Punto actual (vehicle) marker size in pixels
const PUNTO_ACTUAL_MARKER_SIZE = 48;
const PUNTO_ACTUAL_LABEL_OFFSET_Y = 16; // gap between marker and label

/**
 * Overlay that draws a single label below a point (e.g. for puntoActual.nombre).
 */
function createLabelBelowOverlay(maps) {
    function LabelBelowOverlay(position, text, offsetY) {
        this.position = position;
        this.text = text != null ? String(text) : "";
        this.offsetY = offsetY != null ? Number(offsetY) : 0;
        this.div = null;
    }
    LabelBelowOverlay.prototype = Object.create(maps.OverlayView.prototype);
    LabelBelowOverlay.prototype.onAdd = function () {
        if (!this.text) return;
        const div = document.createElement("div");
        div.style.position = "absolute";
        div.style.left = "0";
        div.style.top = "0";
        div.style.pointerEvents = "none";

        const label = document.createElement("div");
        label.textContent = this.text;
        label.style.cssText = [
            "left: 0",
            "top: " + this.offsetY + "px",
            "position: absolute",
            "transform: translateX(-50%)",
            "width: max-content",
            "max-width: 140px",
            "padding: 2px 6px",
            "background: #fff",
            "border-radius: 4px",
            "box-shadow: 0 1px 3px rgba(0,0,0,0.25)",
            "font-size: 11px",
            "font-weight: 600",
            "color: #333",
            "text-align: center",
            "white-space: nowrap",
            "overflow: hidden",
            "text-overflow: ellipsis",
        ].join(";");
        div.appendChild(label);
        this.div = div;
        const panes = this.getPanes();
        if (panes && panes.markerLayer) panes.markerLayer.appendChild(div);
    };
    LabelBelowOverlay.prototype.draw = function () {
        if (!this.div || !this.position) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(
            new maps.LatLng(this.position.lat, this.position.lng)
        );
        if (!point) return;
        this.div.style.left = point.x + "px";
        this.div.style.top = point.y + "px";
    };
    LabelBelowOverlay.prototype.onRemove = function () {
        if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div);
        this.div = null;
    };
    return LabelBelowOverlay;
}

/**
 * Overlay that only draws the title label below a position (used with Marker + clusterer).
 */
function createTitleOnlyOverlay(maps) {
    function TitleOnlyOverlay(position, title, onClick) {
        this.position = position;
        this.title = title != null ? String(title) : "";
        this.onClick = onClick;
        this.div = null;
    }
    TitleOnlyOverlay.prototype = Object.create(maps.OverlayView.prototype);
    TitleOnlyOverlay.prototype.onAdd = function () {
        if (!this.title) return;
        const div = document.createElement("div");
        div.style.position = "absolute";
        div.style.cursor = "pointer";
        div.style.left = "0";
        div.style.top = "0";
        div.style.pointerEvents = "auto";

        const label = document.createElement("div");
        label.textContent = this.title;
        label.style.cssText = [
            "left: 0",
            "top: " + LABEL_OFFSET_Y + "px",
            "position: absolute",
            "transform: translateX(-50%)",
            "width: max-content",
            "max-width: 120px",
            "padding: 2px 6px",
            "background: #fff",
            "border-radius: 4px",
            "box-shadow: 0 1px 3px rgba(0,0,0,0.25)",
            "font-size: 11px",
            "font-weight: 600",
            "color: #333",
            "text-align: center",
            "white-space: nowrap",
            "overflow: hidden",
            "text-overflow: ellipsis",
        ].join(";");
        div.appendChild(label);

        div.addEventListener("click", (e) => {
            e.stopPropagation();
            if (typeof this.onClick === "function") this.onClick();
        });

        this.div = div;
        const panes = this.getPanes();
        if (panes && panes.markerLayer) panes.markerLayer.appendChild(div);
    };
    TitleOnlyOverlay.prototype.draw = function () {
        if (!this.div || !this.position) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(
            new maps.LatLng(this.position.lat, this.position.lng)
        );
        if (!point) return;
        this.div.style.left = point.x + "px";
        this.div.style.top = point.y + "px";
    };
    TitleOnlyOverlay.prototype.onRemove = function () {
        if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div);
        this.div = null;
    };
    return TitleOnlyOverlay;
}

/**
 * Returns a Google Maps icon as data URL for a native-style teardrop pin with the given color.
 * @param {string} hexColor - e.g. "#D11F0F"
 */
function getColoredMarkerIcon(hexColor) {
    const color = hexColor || "#D11F0F";
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
  <defs>
    <filter id="shadow" x="-20%" y="0" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
    </filter>
    <linearGradient id="shine" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" style="stop-color:white;stop-opacity:0.25"/>
      <stop offset="100%" style="stop-color:white;stop-opacity:0"/>
    </linearGradient>
  </defs>
  <path filter="url(#shadow)" fill="${color}" stroke="rgba(0,0,0,0.25)" stroke-width="1.2" stroke-linejoin="round"
    d="M20 2C10.06 2 2 10.5 2 21c0 11 18 29 18 29s18-18 18-29C38 10.5 29.94 2 20 2z"/>
  <path fill="url(#shine)" stroke="none" opacity="0.9"
    d="M20 2C10.06 2 2 10.5 2 21c0 11 18 29 18 29s18-18 18-29C38 10.5 29.94 2 20 2z"/>
  <circle cx="20" cy="18" r="7" fill="white" stroke="rgba(0,0,0,0.12)" stroke-width="0.8"/>
</svg>`;
    return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: new window.google.maps.Size(PIN_WIDTH, PIN_HEIGHT),
        anchor: new window.google.maps.Point(PIN_WIDTH / 2, PIN_HEIGHT),
    };
}

const LOADING_HTML = '<div style="padding:8px 12px;min-width:120px;text-align:center;color:#666;">Loading...</div>';
const ERROR_HTML = '<div style="padding:8px 12px;min-width:120px;text-align:center;color:#c00;">Failed to load info</div>';

/** Detail-route polyline (Google Directions). */
const DETAIL_ROUTE_STROKE = "#16a34a";
const DETAIL_ROUTE_ARROW_REPEAT = "88px";

/** Vehicle GPS track from `obtenerHistorico` (lat/lng points). */
const HISTORIC_VEHICLE_PATH_STROKE = "#dc2626";
const HISTORIC_VEHICLE_ARROW_REPEAT = "96px";
const HISTORIC_VEHICLE_START_MARKER_COLOR = "#16a34a";
/** Max intermediate waypoints per Directions request (origin + dest are separate). */
const DIRECTIONS_MAX_WAYPOINTS = 23;
/** Max stops covered per segment: origin + 23 waypoints + destination = 25 points. */
const DIRECTIONS_MAX_STOPS_PER_SEGMENT = 25;

/**
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number}
 */
function haversineMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toR = (d) => (d * Math.PI) / 180;
    const dLat = toR(lat2 - lat1);
    const dLng = toR(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Straight-line distance along vertices (fallback when Directions fails).
 * @param {Array<{ lat: number, lng: number }>} path
 * @returns {number}
 */
function pathVertexStraightLineMeters(path) {
    if (!path || path.length < 2) return 0;
    let m = 0;
    for (let i = 1; i < path.length; i++) {
        m += haversineMeters(path[i - 1].lat, path[i - 1].lng, path[i].lat, path[i].lng);
    }
    return m;
}

/**
 * @param {{ legs?: Array<{ distance?: { value?: number } }> }|undefined} route
 * @returns {number}
 */
function sumRouteLegsDistanceMeters(route) {
    if (!route?.legs?.length) return 0;
    let m = 0;
    for (const leg of route.legs) {
        if (leg?.distance?.value != null) m += leg.distance.value;
    }
    return m;
}

/**
 * Order stops by `orden` when every row has a numeric `orden`; otherwise keep array order.
 * @param {Array<object>} items
 */
function orderDetailItemsForRoute(items) {
    if (!Array.isArray(items) || items.length === 0) return [];
    const copy = [...items];
    const allHaveOrden = copy.every(
        (x) =>
            x != null &&
            typeof x === "object" &&
            typeof x.orden === "number" &&
            !Number.isNaN(x.orden)
    );
    if (allHaveOrden) {
        copy.sort((a, b) => a.orden - b.orden);
    }
    return copy;
}

/**
 * @param {Array<object>} items
 * @returns {Array<{ lat: number, lng: number }>}
 */
function detailItemsToLatLngPath(items) {
    const ordered = orderDetailItemsForRoute(items);
    const path = [];
    for (const row of ordered) {
        if (row == null || typeof row !== "object") continue;
        const lat = parseFloat(row.latitud ?? row.lat ?? row.Latitud);
        const lng = parseFloat(row.longitud ?? row.lng ?? row.Longitud);
        if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
            continue;
        }
        path.push({ lat, lng });
    }
    return path;
}

/**
 * @param {Array<{lat:number,lng:number}>} path
 * @returns {Array<{ origin, destination, waypoints: Array<{lat,lng}> }>}
 */
function splitPathForDirectionsSegments(path) {
    if (path.length < 2) return [];
    const segments = [];
    let start = 0;
    while (start < path.length - 1) {
        const end = Math.min(start + (DIRECTIONS_MAX_STOPS_PER_SEGMENT - 1), path.length - 1);
        const origin = path[start];
        const destination = path[end];
        const between = path.slice(start + 1, end);
        const waypoints = between.slice(0, DIRECTIONS_MAX_WAYPOINTS);
        segments.push({ origin, destination, waypoints });
        start = end;
    }
    return segments;
}

/**
 * Fetches driving directions polyline(s) from Google; concatenates segments when >25 stops.
 * @param {object} maps - `window.google.maps`
 * @param {Array<{lat:number,lng:number}>} pathPoints
 * @param {(latLngPath: Array<{lat:number,lng:number}>|null, distanceMeters: number|null) => void} onDone
 */
function fetchDirectionsDrivingPath(maps, pathPoints, onDone) {
    if (!pathPoints || pathPoints.length < 2) {
        onDone(null, null);
        return;
    }
    const svc = new maps.DirectionsService();
    const segments = splitPathForDirectionsSegments(pathPoints);

    let combinedFlat = [];
    let totalMeters = 0;

    const runSegment = (index) => {
        if (index >= segments.length) {
            if (combinedFlat.length >= 2) {
                onDone(combinedFlat, totalMeters);
            } else {
                onDone(null, null);
            }
            return;
        }
        const { origin, destination, waypoints } = segments[index];
        const wps = waypoints.map((p) => ({ location: p, stopover: true }));
        svc.route(
            {
                origin,
                destination,
                waypoints: wps,
                travelMode: maps.TravelMode.DRIVING,
                unitSystem: maps.UnitSystem.METRIC,
                optimizeWaypoints: false,
                provideRouteAlternatives: false,
            },
            (result, status) => {
                if (status !== maps.DirectionsStatus.OK || !result.routes?.[0]) {
                    onDone(null, null);
                    return;
                }
                const route = result.routes[0];
                totalMeters += sumRouteLegsDistanceMeters(route);
                const overview = route.overview_path || [];
                const pts = overview.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
                if (combinedFlat.length > 0 && pts.length > 0) {
                    const last = combinedFlat[combinedFlat.length - 1];
                    const first = pts[0];
                    if (last.lat === first.lat && last.lng === first.lng) {
                        pts.shift();
                    }
                }
                combinedFlat = combinedFlat.concat(pts);
                runSegment(index + 1);
            }
        );
    };

    runSegment(0);
}

/**
 * @param {unknown} raw — API array of { lat, lng, ... }
 * @returns {{ path: Array<{ lat: number, lng: number }>, startPoint: { lat: number, lng: number, timeText: string } | null }}
 */
function historicRawToLatLngPath(raw) {
    if (!Array.isArray(raw) || raw.length === 0) return { path: [], startPoint: null };
    const path = [];
    let startPoint = null;

    const getHistoricTimeText = (point) => {
        if (point == null || typeof point !== "object") return "";
        // console.log('point');
        // console.log(point);
        const rawTime =
            point.fechaHora;
        if (rawTime == null || String(rawTime).trim() === "") return "";
        const value = String(rawTime).trim();
        const parsed = moment(value, moment.ISO_8601, true);
        if (parsed.isValid()) return parsed.format("HH:mm:ss");
        const flexible = moment(value);
        if (flexible.isValid()) return flexible.format("HH:mm:ss");
        return value;
    };

    for (const p of raw) {
        if (p == null || typeof p !== "object") continue;
        const lat = parseFloat(p.lat ?? p.Lat ?? p.latitud);
        const lng = parseFloat(p.lng ?? p.Lng ?? p.longitud);
        if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) continue;
        path.push({ lat, lng });
        if (startPoint == null) {
            startPoint = {
                lat,
                lng,
                timeText: getHistoricTimeText(p),
            };
        }
    }
    return { path, startPoint };
}

/**
 * Google Map component dedicated to the Mapa Seguimiento page.
 *
 * @param {Array} markers - Array of { lat, lng, title?, infoWindow?, color?, index? }
 * @param {Object} center - { lat, lng } or null
 * @param {number} zoom - Zoom level
 * @param {Function} onMarkerClick - (markerData) => void
 * @param {Function} onMapClick - (lat, lng) => void
 * @param {Function} fetchMarkerInfo - async (markerData) => string | { content: string }. Shown in InfoWindow on marker click.
 * @param {Object} puntoActual - Current vehicle position. When it changes, the map updates the vehicle marker.
 * @param {Array<object>|null} [detailItems] - Route stops; when set, draws **driving** directions (Google Directions API) in order, with arrows.
 * @param {string} fechaEntrega - Fecha de entrega.
 */
export default function MapaSeguimientoGoogle({
    markers = [],
    center = null,
    zoom = 12,
    onMarkerClick,
    onMapClick,
    fetchMarkerInfo,
    puntoActual = null,
    detailItems = [],
    fechaEntrega = "",
}) {
    const { t } = useTranslation();
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const markersRef = useRef([]);
    const titleOverlaysRef = useRef([]);
    const infoWindowsRef = useRef([]);
    const clustererRef = useRef(null);
    const puntoActualMarkerRef = useRef(null);
    const puntoActualLabelRef = useRef(null);
    const detailRoutePolylineRef = useRef(null);
    const historicVehiclePolylineRef = useRef(null);
    const historicVehicleStartMarkerRef = useRef(null);
    const historicVehicleStartInfoWindowRef = useRef(null);
    const historicoFetchGenerationRef = useRef(0);
    const [historicVehiclePath, setHistoricVehiclePath] = useState([]);
    const [historicVehicleStartPoint, setHistoricVehicleStartPoint] = useState(null);
    const [plannedRouteKm, setPlannedRouteKm] = useState(null);
    const [routeExecutionKm, setRouteExecutionKm] = useState(null);
    const defaultHistoricoDate = moment(fechaEntrega).isValid()
        ? moment(fechaEntrega).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD");
    const [historicoDateIni, setHistoricoDateIni] = useState(defaultHistoricoDate);
    const [historicoHoraIni, setHistoricoHoraIni] = useState("00:00");
    const [historicoDateFin, setHistoricoDateFin] = useState(defaultHistoricoDate);
    const [historicoHoraFin, setHistoricoHoraFin] = useState("23:59");
    const [appliedHistoricoRange, setAppliedHistoricoRange] = useState({
        dateIni: defaultHistoricoDate,
        horaIni: "00:00",
        dateFin: defaultHistoricoDate,
        horaFin: "23:59",
    });
    const [historicoFiltersVisible, setHistoricoFiltersVisible] = useState(false);
    const lastCenterZoomRef = useRef({ lat: null, lng: null, zoom: null });

    useEffect(() => {
        const nextDate = moment(fechaEntrega).isValid()
            ? moment(fechaEntrega).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD");
        setHistoricoDateIni(nextDate);
        setHistoricoHoraIni("00:00");
        setHistoricoDateFin(nextDate);
        setHistoricoHoraFin("23:59");
        setAppliedHistoricoRange({
            dateIni: nextDate,
            horaIni: "00:00",
            dateFin: nextDate,
            horaFin: "23:59",
        });
    }, [fechaEntrega]);

    useEffect(() => {
        const items = detailItems == null ? [] : detailItems;
        if (!Array.isArray(items) || items.length === 0) {
            setPlannedRouteKm(null);
        }
    }, [detailItems]);

    // Driving route along detail stops (Google Directions); cleared when detailItems is empty/null. Falls back to straight segments if Directions fails.
    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;

        const clearPolyline = () => {
            if (detailRoutePolylineRef.current) {
                detailRoutePolylineRef.current.setMap(null);
                detailRoutePolylineRef.current = null;
            }
        };

        clearPolyline();
        setPlannedRouteKm(null);

        const items = detailItems == null ? [] : detailItems;
        if (!Array.isArray(items) || items.length === 0) {
            return () => clearPolyline();
        }

        const vertexPath = detailItemsToLatLngPath(items);
        if (vertexPath.length < 2) {
            return () => clearPolyline();
        }

        let cancelled = false;
        const maps = window.google.maps;

        const drawPolyline = (pathLatLng) => {
            if (cancelled || !pathLatLng || pathLatLng.length < 2) return;
            clearPolyline();
            const polyline = new maps.Polyline({
                path: pathLatLng,
                geodesic: true,
                strokeColor: DETAIL_ROUTE_STROKE,
                strokeOpacity: 0.95,
                strokeWeight: 4,
                icons: [
                    {
                        icon: {
                            path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                            scale: 2.5,
                            strokeColor: DETAIL_ROUTE_STROKE,
                            strokeWeight: 1.5,
                            fillColor: DETAIL_ROUTE_STROKE,
                            fillOpacity: 1,
                        },
                        offset: "0",
                        repeat: DETAIL_ROUTE_ARROW_REPEAT,
                    },
                ],
                map,
                zIndex: 1,
            });
            detailRoutePolylineRef.current = polyline;
        };

        fetchDirectionsDrivingPath(maps, vertexPath, (drivingPath, distanceMeters) => {
            if (cancelled) return;
            if (drivingPath != null && drivingPath.length >= 2) {
                drawPolyline(drivingPath);
                setPlannedRouteKm(
                    typeof distanceMeters === "number" && !Number.isNaN(distanceMeters)
                        ? distanceMeters / 1000
                        : null
                );
            } else {
                drawPolyline(vertexPath);
            }
        });

        return () => {
            cancelled = true;
            clearPolyline();
        };
    }, [map, detailItems]);

    // Red GPS track from obtenerHistorico; cleared when `historicVehiclePath` is empty
    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;

        const clearHistoricPolyline = () => {
            if (historicVehiclePolylineRef.current) {
                historicVehiclePolylineRef.current.setMap(null);
                historicVehiclePolylineRef.current = null;
            }
            if (historicVehicleStartInfoWindowRef.current) {
                historicVehicleStartInfoWindowRef.current.close();
                historicVehicleStartInfoWindowRef.current = null;
            }
            if (historicVehicleStartMarkerRef.current) {
                historicVehicleStartMarkerRef.current.setMap(null);
                historicVehicleStartMarkerRef.current = null;
            }
        };

        clearHistoricPolyline();

        if (!Array.isArray(historicVehiclePath) || historicVehiclePath.length < 2) {
            return () => clearHistoricPolyline();
        }

        const maps = window.google.maps;
        const polyline = new maps.Polyline({
            path: historicVehiclePath,
            geodesic: true,
            strokeColor: HISTORIC_VEHICLE_PATH_STROKE,
            strokeOpacity: 1,
            strokeWeight: 3,
            icons: [
                {
                    icon: {
                        path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 2.5,
                        strokeColor: HISTORIC_VEHICLE_PATH_STROKE,
                        strokeWeight: 1.5,
                        fillColor: HISTORIC_VEHICLE_PATH_STROKE,
                        fillOpacity: 1,
                    },
                    offset: "0",
                    repeat: HISTORIC_VEHICLE_ARROW_REPEAT,
                },
            ],
            map,
            zIndex: 2,
        });
        historicVehiclePolylineRef.current = polyline;

        if (historicVehicleStartPoint != null) {
            const marker = new maps.Marker({
                position: {
                    lat: historicVehicleStartPoint.lat,
                    lng: historicVehicleStartPoint.lng,
                },
                map,
                title:
                    historicVehicleStartPoint.timeText && historicVehicleStartPoint.timeText !== ""
                        ? `${t("label_inicio")}: ${historicVehicleStartPoint.timeText}`
                        : t("label_inicio"),
                icon: getColoredMarkerIcon(HISTORIC_VEHICLE_START_MARKER_COLOR),
                zIndex: 3,
            });
            historicVehicleStartMarkerRef.current = marker;

            const infoContent =
                historicVehicleStartPoint.timeText && historicVehicleStartPoint.timeText !== ""
                    ? `<div style="padding:6px 8px;font-size:12px;"><strong>${t("label_inicio")}:</strong> ${historicVehicleStartPoint.timeText}</div>`
                    : `<div style="padding:6px 8px;font-size:12px;"><strong>${t("label_inicio")}</strong></div>`;
            const infoWindow = new maps.InfoWindow({ content: infoContent, disableAutoPan: true });
            historicVehicleStartInfoWindowRef.current = infoWindow;

            marker.addListener("click", () => {
                infoWindow.open({ anchor: marker, map });
            });
        }

        return () => {
            clearHistoricPolyline();
        };
    }, [map, historicVehiclePath, historicVehicleStartPoint, t]);

    // Historic GPS polyline for the selected day + vehicle.
    // Poll every 15s and restart/stop polling when date or vehicle plate changes.
    useEffect(() => {
        setHistoricVehiclePath([]);
        setHistoricVehicleStartPoint(null);
        setRouteExecutionKm(null);

        if (puntoActual == null || typeof puntoActual !== "object" || !moment(fechaEntrega).isValid()) {
            return;
        }

        const placa =
            puntoActual.placa ?? puntoActual.valuePlaca ?? puntoActual.PLACA ?? puntoActual.labelPlaca;
        if (placa == null || String(placa).trim() === "") {
            return;
        }

        const fechaIniMoment = moment(
            `${appliedHistoricoRange.dateIni} ${appliedHistoricoRange.horaIni}`,
            "YYYY-MM-DD HH:mm",
            true
        );
        const fechaFinMoment = moment(
            `${appliedHistoricoRange.dateFin} ${appliedHistoricoRange.horaFin}`,
            "YYYY-MM-DD HH:mm",
            true
        );
        if (!fechaIniMoment.isValid() || !fechaFinMoment.isValid()) {
            return;
        }

        const objHistorico = {
            fechaHoraIni: fechaIniMoment.format("DD/MM/YYYY HH:mm:ss"),
            fechaHoraFin: fechaFinMoment.format("DD/MM/YYYY HH:mm:ss"),
            placa: String(placa).trim(),
        };

        const objRecorrido = {
            fechaHoraFin: fechaFinMoment.format("YYYY-MM-DD HH:mm:ss"),
            fechaHoraInicio: fechaIniMoment.format("YYYY-MM-DD HH:mm:ss"),
            placa: String(placa).trim(),
            idEmpresa: sharedObtenerIdEmpresa(),
        };

        const gen = (historicoFetchGenerationRef.current += 1);
        let cancelled = false;
        let polling = false;
        let intervalId = null;

        const run = async () => {
            if (cancelled || polling) return;
            polling = true;
            try {
                const responseHistorico = await obtenerHistorico(objHistorico);
                const responseInfoKilometraje = await obtenerReporteInfoKilometraje(objRecorrido);
                // console.log('responseInfoKilometraje');
                // console.log(responseInfoKilometraje);
                if (cancelled || gen !== historicoFetchGenerationRef.current) return;

                if (
                    responseHistorico.error === false &&
                    Array.isArray(responseHistorico.data) &&
                    responseHistorico.data.length > 0
                ) {
                    const raw = responseHistorico.data;
                    const { path, startPoint } = historicRawToLatLngPath(raw);
                    if (cancelled || gen !== historicoFetchGenerationRef.current) return;
                    if (path.length >= 2) {
                        setHistoricVehiclePath(path);
                        setHistoricVehicleStartPoint(startPoint);
                        if (responseInfoKilometraje.error === false && responseInfoKilometraje.data) {
                            setRouteExecutionKm(responseInfoKilometraje.data[objRecorrido.placa].kilometrajeTotal);
                        }
                    } else {
                        setHistoricVehiclePath([]);
                        setHistoricVehicleStartPoint(null);
                        setRouteExecutionKm(null);
                    }
                } else {
                    if (cancelled || gen !== historicoFetchGenerationRef.current) return;
                    setHistoricVehiclePath([]);
                    setHistoricVehicleStartPoint(null);
                    setRouteExecutionKm(null);
                }
            } catch (_) {
                if (cancelled || gen !== historicoFetchGenerationRef.current) return;
                setHistoricVehiclePath([]);
                setHistoricVehicleStartPoint(null);
                setRouteExecutionKm(null);
            } finally {
                polling = false;
            }
        };

        run();
        intervalId = window.setInterval(run, 15000);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only when date or vehicle identity changes
        return () => {
            cancelled = true;
            if (intervalId != null) {
                window.clearInterval(intervalId);
            }
            // Invalidate in-flight responses from previous plate/date cycle.
            historicoFetchGenerationRef.current += 1;
        };
    }, [
        appliedHistoricoRange,
        puntoActual?.placa,
        puntoActual?.valuePlaca,
        puntoActual?.PLACA,
        puntoActual?.labelPlaca,
    ]);

    const handleActualizarHistoricoParametros = () => {
        setAppliedHistoricoRange({
            dateIni: historicoDateIni,
            horaIni: historicoHoraIni,
            dateFin: historicoDateFin,
            horaFin: historicoHoraFin,
        });
    };

    // Load MarkerClusterer library
    useEffect(() => {
        if (
            typeof window !== "undefined" &&
            window.google?.maps &&
            !window.MarkerClusterer &&
            !window.markerClustererLoading
        ) {
            window.markerClustererLoading = true;
            const script = document.createElement("script");
            script.src = "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js";
            script.async = true;
            script.onload = () => {
                window.markerClustererLoaded = true;
            };
            script.onerror = () => {
                window.markerClustererLoading = false;
            };
            document.head.appendChild(script);
        }
        // console.log('markers', markers);
    }, []);

    // Initialize map once when container is mounted and Google is available
    useEffect(() => {
        if (!window.google || !window.google.maps || !mapRef.current) return;

        const defaultCenter = { lat: -16.5, lng: -63.6 };
        const initialCenter = center || defaultCenter;

        const googleMap = new window.google.maps.Map(mapRef.current, {
            center: initialCenter,
            zoom: zoom,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
        });

        if (typeof onMapClick === "function") {
            googleMap.addListener("click", (event) => {
                onMapClick(event.latLng.lat(), event.latLng.lng());
            });
        }

        setMap(googleMap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update center/zoom only when they actually change (so puntoActual updates don't recenter the map)
    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return;
        const lat = center?.lat != null ? parseFloat(center.lat) : NaN;
        const lng = center?.lng != null ? parseFloat(center.lng) : NaN;
        const zoomVal = zoom != null ? Number(zoom) : null;
        const prev = lastCenterZoomRef.current;
        const centerChanged = center != null && !Number.isNaN(lat) && !Number.isNaN(lng) &&
            (prev.lat !== lat || prev.lng !== lng);
        const zoomChanged = zoomVal != null && prev.zoom !== zoomVal;

        if (centerChanged) {
            map.panTo(new window.google.maps.LatLng(lat, lng));
            lastCenterZoomRef.current = { ...prev, lat, lng };
        }
        if (zoomChanged) {
            map.setZoom(zoomVal);
            lastCenterZoomRef.current = { ...lastCenterZoomRef.current, zoom: zoomVal };
        }
    }, [map, center, zoom]);

    // When puntoActual changes: add/update or remove the vehicle position marker
    useEffect(() => {
        if (!map || !window.google?.maps) return;

        const { maps } = window.google;

        if (puntoActualMarkerRef.current) {
            puntoActualMarkerRef.current.setMap(null);
            puntoActualMarkerRef.current = null;
        }
        if (puntoActualLabelRef.current) {
            puntoActualLabelRef.current.setMap(null);
            puntoActualLabelRef.current = null;
        }

        if (puntoActual == null || typeof puntoActual !== "object") return;

        const lat = parseFloat(puntoActual.LATITUD ?? puntoActual.lat ?? puntoActual.latitud);
        const lng = parseFloat(puntoActual.LONGITUD ?? puntoActual.lng ?? puntoActual.longitud);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;

        const iconUrl = sharedObtenerIcon(
            puntoActual.ORIENT,
            false,
            puntoActual.I_ENCENDIDO,
            puntoActual.VELOCIDAD,
            puntoActual.icono
        );

        // console.log('puntoActual=>', puntoActual);
        // console.log('fechaEntrega=>', fechaEntrega);

        const marker = new maps.Marker({
            map,
            position: { lat, lng },
            title: "Posición actual del vehículo",
            icon: {
                url: iconUrl,
                scaledSize: new maps.Size(PUNTO_ACTUAL_MARKER_SIZE, PUNTO_ACTUAL_MARKER_SIZE),
                anchor: new maps.Point(PUNTO_ACTUAL_MARKER_SIZE / 2, PUNTO_ACTUAL_MARKER_SIZE / 2),
            },
        });
        puntoActualMarkerRef.current = marker;

        const nombre = puntoActual.nombre ?? puntoActual.NOMBRE ?? "";
        if (nombre) {
            const LabelBelowOverlay = createLabelBelowOverlay(maps);
            const labelOverlay = new LabelBelowOverlay(
                { lat, lng },
                nombre,
                PUNTO_ACTUAL_LABEL_OFFSET_Y
            );
            labelOverlay.setMap(map);
            puntoActualLabelRef.current = labelOverlay;
        }
    }, [map, puntoActual]);

    // Render markers with clustering: Marker (pin) + TitleOnlyOverlay (title below); sync overlay visibility with cluster state
    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return;

        const { maps } = window.google;

        if (clustererRef.current) {
            try {
                clustererRef.current.clearMarkers();
            } catch (_) { }
            clustererRef.current = null;
        }
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];
        titleOverlaysRef.current.forEach((o) => o.setMap(null));
        titleOverlaysRef.current = [];
        infoWindowsRef.current.forEach((iw) => iw.close());
        infoWindowsRef.current = [];

        const TitleOnlyOverlay = createTitleOnlyOverlay(maps);
        const newMarkers = [];
        const newTitleOverlays = [];
        const newInfoWindows = [];

        const syncTitleOverlaysVisibility = () => {
            newMarkers.forEach((marker, i) => {
                const overlay = newTitleOverlays[i];
                if (overlay) overlay.setMap(marker.getMap() ? map : null);
            });
        };

        markers.forEach((item) => {
            const lat = parseFloat(item.lat);
            const lng = parseFloat(item.lng);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return;

            const position = { lat, lng };
            const icon = getColoredMarkerIcon(item.color);
            const fallbackContent = item.infoWindow != null ? item.infoWindow : item.title || "";
            const infoWindow = new maps.InfoWindow({ content: fallbackContent });

            const openInfo = async () => {
                infoWindowsRef.current.forEach((iw) => iw.close());
                infoWindow.setPosition(position);
                infoWindow.open(map);

                if (typeof fetchMarkerInfo === "function") {
                    infoWindow.setContent(LOADING_HTML);
                    try {
                        const result = await fetchMarkerInfo(item);
                        const content =
                            typeof result === "string"
                                ? result
                                : result && typeof result.content === "string"
                                    ? result.content
                                    : fallbackContent;
                        infoWindow.setContent(content);
                    } catch (err) {
                        console.warn("fetchMarkerInfo failed:", err);
                        infoWindow.setContent(ERROR_HTML);
                    }
                } else {
                    infoWindow.setContent(fallbackContent);
                }

                if (typeof onMarkerClick === "function") onMarkerClick(item);
            };

            const marker = new maps.Marker({
                map,
                position,
                title: item.title || "",
                icon,
            });
            marker.addListener("click", openInfo);

            const titleOverlay = new TitleOnlyOverlay(position, item.title, openInfo);
            titleOverlay.setMap(map);

            newMarkers.push(marker);
            newTitleOverlays.push(titleOverlay);
            newInfoWindows.push(infoWindow);
        });

        markersRef.current = newMarkers;
        titleOverlaysRef.current = newTitleOverlays;
        infoWindowsRef.current = newInfoWindows;

        const initClusterer = () => {
            if (window.markerClusterer?.MarkerClusterer && newMarkers.length > 0) {
                const clusterer = new window.markerClusterer.MarkerClusterer({
                    map,
                    markers: newMarkers,
                });
                clusterer.addListener("clusteringend", syncTitleOverlaysVisibility);
                setTimeout(syncTitleOverlaysVisibility, 100);
                clustererRef.current = clusterer;
                return true;
            }
            if (window.MarkerClusterer && newMarkers.length > 0) {
                const clusterer = new window.MarkerClusterer(map, newMarkers, {
                    imagePath:
                        "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
                });
                window.google.maps.event.addListener(clusterer, "clusteringend", syncTitleOverlaysVisibility);
                setTimeout(syncTitleOverlaysVisibility, 100);
                clustererRef.current = clusterer;
                return true;
            }
            return false;
        };

        if (!initClusterer() && window.markerClustererLoading) {
            const retry = () => {
                if (initClusterer()) return;
                setTimeout(retry, 500);
            };
            setTimeout(retry, 800);
        } else if (!clustererRef.current) {
            syncTitleOverlaysVisibility();
        }
    }, [map, markers, onMarkerClick, fetchMarkerInfo]);

    return (
        <div className="relative w-full h-full" style={{ minHeight: "400px" }}>
            <div
                ref={mapRef}
                id="mapa-seguimiento-google"
                className="w-full h-full"
            />

            {/* Route color legend (top-left) */}
            <div className="absolute top-3 left-3 z-10 bg-white/95 border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs text-gray-700 mt-12">
                <div className="flex items-center gap-2 whitespace-nowrap">
                    <span
                        className="inline-block w-6 h-[3px] rounded"
                        style={{ backgroundColor: DETAIL_ROUTE_STROKE }}
                        aria-hidden
                    />
                    <span>
                        {t("label_ruta_planificada")}
                        {plannedRouteKm != null && (
                            <>
                                {" "}
                                <span className="font-semibold tabular-nums">
                                    ({plannedRouteKm.toFixed(2)} km)
                                </span>
                            </>
                        )}
                    </span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap mt-1">
                    <span
                        className="inline-block w-6 h-[3px] rounded"
                        style={{ backgroundColor: HISTORIC_VEHICLE_PATH_STROKE }}
                        aria-hidden
                    />
                    <span>
                        {t("label_ruta_vehiculo")}
                        {routeExecutionKm != null && (
                            <>
                                {" "}
                                <span className="font-semibold tabular-nums">
                                    ({routeExecutionKm.toFixed(2)} km)
                                </span>
                            </>
                        )}
                    </span>
                </div>
            </div>

            {/* Historic range controls toggle (top-right) */}
            <button
                type="button"
                onClick={() => setHistoricoFiltersVisible((v) => !v)}
                className="absolute top-3 right-3 z-10 mt-12 bg-white/95 border border-gray-200 rounded-md shadow-sm p-2 hover:bg-white text-gray-700"
                title={t("label_configuracion")}
                aria-label={t("label_configuracion")}
            >
                {historicoFiltersVisible ? <X className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
            </button>

            {/* Historic range controls panel (hidden by default) */}
            {historicoFiltersVisible && (
                <div className="absolute top-3 right-3 z-10 bg-white/95 border border-gray-200 rounded-md shadow-sm p-3 text-xs text-gray-700 mt-24 w-[320px]">
                    <div className="grid grid-cols-[64px_1fr_90px] gap-2 items-center">
                        <span className="font-medium">{t("label_inicio")}</span>
                        <Calendar
                            value={historicoDateIni}
                            onChange={(v) => setHistoricoDateIni(v)}
                            format="YYYY-MM-DD"
                            placeholder="YYYY-MM-DD"
                        />
                        <input
                            type="time"
                            value={historicoHoraIni}
                            onChange={(e) => setHistoricoHoraIni(e.target.value)}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                    </div>
                    <div className="grid grid-cols-[64px_1fr_90px] gap-2 items-center mt-2">
                        <span className="font-medium">{t("label_fin")}</span>
                        <Calendar
                            value={historicoDateFin}
                            onChange={(v) => setHistoricoDateFin(v)}
                            format="YYYY-MM-DD"
                            placeholder="YYYY-MM-DD"
                        />
                        <input
                            type="time"
                            value={historicoHoraFin}
                            onChange={(e) => setHistoricoHoraFin(e.target.value)}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleActualizarHistoricoParametros}
                        className="mt-3 w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                    >
                        {t("label_actualizar")}
                    </button>
                </div>
            )}
        </div>
    );
}
